import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from '../contexts/WebSocketContext';
import { wsjFeedService } from '../services/wsjFeedService';
import type { FeedMessage, PaginatedFeedResponse } from '../services/wsjFeedService';

interface UseWsjFeedOptions {
  isNearTop: boolean;
  pageSize?: number;
  onNewMessage?: (msg: FeedMessage) => void;
}

interface UseWsjFeedReturn {
  messages: FeedMessage[];
  isLoading: boolean;
  isConnected: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  reachedDayEnd: boolean;
  pendingNewCount: number;
  pendingSinceTime: string | null;
  clearPendingNew: () => void;
}

/** Fallback only while page 1 is not yet in the query cache. */
const MAX_UNCACHED_REALTIME = 50;

export function prependFeedMessage(
  old: { pages: PaginatedFeedResponse[]; pageParams: number[] } | undefined,
  msg: FeedMessage,
): { pages: PaginatedFeedResponse[]; pageParams: number[] } | undefined {
  if (!old?.pages.length) return old;
  if (old.pages.some((page) => page.results.some((m) => m.id === msg.id))) {
    return old;
  }
  const [first, ...rest] = old.pages;
  return {
    ...old,
    pages: [{ ...first, results: [msg, ...first.results] }, ...rest],
  };
}

export function useWsjFeed({ isNearTop, pageSize = 20, onNewMessage }: UseWsjFeedOptions): UseWsjFeedReturn {
  const { subscribe, isConnected } = useWebSocket();
  const queryClient = useQueryClient();
  const [realtimeMessages, setRealtimeMessages] = useState<FeedMessage[]>([]);
  const [pendingNewCount, setPendingNewCount] = useState(0);
  const [pendingSinceTime, setPendingSinceTime] = useState<string | null>(null);
  const isNearTopRef = useRef(isNearTop);
  const onNewMessageRef = useRef(onNewMessage);

  // Keep refs in sync
  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  useEffect(() => {
    isNearTopRef.current = isNearTop;
  }, [isNearTop]);

  // Paginated query - API returns newest first
  const {
    data,
    fetchNextPage,
    hasNextPage: rawHasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ['wsj-feed', pageSize],
    queryFn: async ({ pageParam = 1 }: { pageParam: number }) => {
      return wsjFeedService.getMessages(pageParam, pageSize);
    },
    getNextPageParam: (lastPage: PaginatedFeedResponse) => {
      if (lastPage.next) {
        // Extract page number from next URL
        const url = new URL(lastPage.next, window.location.origin);
        const nextPage = url.searchParams.get('page');
        return nextPage ? parseInt(nextPage, 10) : undefined;
      }
      return undefined;
    },
    initialPageParam: 1,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Once page 1 exists, live rows live in the cache. Drop the uncached buffer.
  useEffect(() => {
    if (data?.pages[0]) {
      setRealtimeMessages((prev) => (prev.length === 0 ? prev : []));
    }
  }, [data]);

  // Subscribe to WebSocket
  useEffect(() => {
    return subscribe('wsj.feed', (event: { data: FeedMessage & { message_type: string } }) => {
      const msg = event.data;
      if (msg.message_type === 'feed.new') {
        let absorbed = false;
        queryClient.setQueryData<{ pages: PaginatedFeedResponse[]; pageParams: number[] }>(
          ['wsj-feed', pageSize],
          (old) => {
            if (old?.pages.length) absorbed = true;
            return prependFeedMessage(old, msg);
          }
        );
        // Page 1 not cached yet — hold a short buffer until the first fetch lands.
        if (!absorbed) {
          setRealtimeMessages((prev) => [msg, ...prev].slice(0, MAX_UNCACHED_REALTIME));
        }

        // If not near top, increment pending count and track first arrival time
        if (!isNearTopRef.current) {
          setPendingNewCount(prev => {
            if (prev === 0) {
              setPendingSinceTime(msg.created_at);
            }
            return prev + 1;
          });
        }

        // Notify if user isn't looking at latest messages
        if (document.hidden || !isNearTopRef.current) {
          onNewMessageRef.current?.(msg);
        }
      } else if (msg.message_type === 'feed.updated') {
        // Update in realtime messages
        setRealtimeMessages(prev =>
          prev.map(m => (m.id === msg.id ? { ...m, ...msg } : m))
        );
        // Also update in query cache
        queryClient.setQueryData<{ pages: PaginatedFeedResponse[]; pageParams: number[] }>(
          ['wsj-feed', pageSize],
          (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map(page => ({
                ...page,
                results: page.results.map(m => (m.id === msg.id ? { ...m, ...msg } : m)),
              })),
            };
          }
        );
      }
    });
  }, [subscribe, queryClient, pageSize]);

  // Merge realtime + paginated, dedup by ID, newest first
  const messages = useMemo(() => {
    const paginatedMessages = data?.pages.flatMap(page => page.results) || [];
    const paginatedIds = new Set(paginatedMessages.map(m => m.id));
    const uniqueRealtime = realtimeMessages.filter(m => !paginatedIds.has(m.id));
    return [...uniqueRealtime, ...paginatedMessages];
  }, [data, realtimeMessages]);

  // Day boundary detection: stop loading if oldest message is from a previous day
  const reachedDayEnd = useMemo(() => {
    if (messages.length < pageSize) return false;
    const oldest = messages[messages.length - 1];
    if (!oldest) return false;
    const oldestDate = new Date(oldest.created_at).toDateString();
    const today = new Date().toDateString();
    return oldestDate !== today;
  }, [messages, pageSize]);

  const hasNextPage = rawHasNextPage === true && !reachedDayEnd;

  const clearPendingNew = useCallback(() => {
    setPendingNewCount(0);
    setPendingSinceTime(null);
  }, []);

  return {
    messages,
    isLoading,
    isConnected,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    reachedDayEnd,
    pendingNewCount,
    pendingSinceTime,
    clearPendingNew,
  };
}
